import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { siteAPI } from '../services/api';
import { useNotification } from './NotificationContext';
import { useAuth } from './AuthContext';

export const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const { success, error: showError } = useNotification();
  const { isAuthenticated, user } = useAuth();

  // Fetch all sites with optional filters
  const fetchSites = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await siteAPI.getAllSites(params);
      const siteList = response?.data?.data || response?.data || [];
      setSites(Array.isArray(siteList) ? siteList : []);
      return { success: true, data: siteList };
    } catch (error) {
      console.error('🏢 SiteContext: Error fetching sites:', error);
      if (isAuthenticated) {
        showError(error.response?.data?.message || 'Failed to load sites');
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [showError, isAuthenticated]);

  // Fetch sites when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchSites();
    } else if (!isAuthenticated) {
      setSites((prev) => (prev.length === 0 ? prev : []));
    }
  }, [isAuthenticated, user?.role, fetchSites]);

  // Get single site by ID
  const getSiteById = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await siteAPI.getSiteById(id);
      return { success: true, data: response?.data?.data || response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch site details';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Create new site
  const createSite = useCallback(async (siteData) => {
    try {
      setLoading(true);
      const response = await siteAPI.createSite(siteData);
      success('Site created successfully!');
      await fetchSites();
      return { success: true, data: response?.data?.site || response?.data?.data || response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create site';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  // Update site
  const updateSite = useCallback(async (id, siteData) => {
    try {
      setLoading(true);
      const response = await siteAPI.updateSite(id, siteData);
      success('Site updated successfully!');
      await fetchSites();
      return { success: true, data: response?.data?.site || response?.data?.data || response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update site';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  // Delete site (Non-destructive unlinking)
  const deleteSite = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await siteAPI.deleteSite(id);
      setSites((prev) => prev.filter((s) => s._id !== id));
      success(response?.data?.message || 'Site deleted and staff unlinked successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete site';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [success, showError]);

  // Get site statistics
  const getSiteStats = useCallback(async (id) => {
    try {
      const response = await siteAPI.getSiteStats(id);
      return { success: true, data: response?.data };
    } catch (error) {
      console.error('Error fetching site stats:', error);
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }, []);

  // Get site employees
  const getSiteEmployees = useCallback(async (id, params = {}) => {
    try {
      const response = await siteAPI.getSiteEmployees(id, params);
      return { success: true, data: response?.data?.data || [] };
    } catch (error) {
      console.error('Error fetching site employees:', error);
      return { success: false, error: error.response?.data?.message || error.message, data: [] };
    }
  }, []);

  // Get site supervisors
  const getSiteManagers = useCallback(async (id) => {
    try {
      const response = await siteAPI.getSiteManagers(id);
      return { success: true, data: response?.data?.data || [] };
    } catch (error) {
      console.error('Error fetching site managers:', error);
      return { success: false, error: error.response?.data?.message || error.message, data: [] };
    }
  }, []);

  // Get site attendance records
  const getSiteAttendance = useCallback(async (id, params = {}) => {
    try {
      const response = await siteAPI.getSiteAttendance(id, params);
      return { success: true, data: response?.data?.data || [] };
    } catch (error) {
      console.error('Error fetching site attendance:', error);
      return { success: false, error: error.response?.data?.message || error.message, data: [] };
    }
  }, []);

  // Bulk assign employees to site
  const assignEmployees = useCallback(async (siteId, employeeIds, managerId = null) => {
    try {
      setLoading(true);
      const response = await siteAPI.assignEmployees(siteId, employeeIds, managerId);
      success(response?.data?.message || 'Employees assigned successfully');
      await fetchSites();
      return { success: true, data: response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to assign employees';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  // Bulk unassign employees from site
  const unassignEmployees = useCallback(async (siteId, employeeIds) => {
    try {
      setLoading(true);
      const response = await siteAPI.unassignEmployees(siteId, employeeIds);
      success(response?.data?.message || 'Employees unassigned successfully');
      await fetchSites();
      return { success: true, data: response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to unassign employees';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  // Reassign employees to another site
  const reassignEmployees = useCallback(async (employeeIds, targetSiteId) => {
    try {
      setLoading(true);
      const response = await siteAPI.reassignEmployees(employeeIds, targetSiteId);
      success(response?.data?.message || 'Employees reassigned successfully');
      await fetchSites();
      return { success: true, data: response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reassign employees';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  // Assign supervisor to site
  const assignManager = useCallback(async (siteId, managerId) => {
    try {
      setLoading(true);
      const response = await siteAPI.assignManager(siteId, managerId);
      success(response?.data?.message || 'Supervisor assigned successfully');
      await fetchSites();
      return { success: true, data: response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to assign supervisor';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  // Unassign supervisor from site
  const unassignManager = useCallback(async (siteId, managerId) => {
    try {
      setLoading(true);
      const response = await siteAPI.unassignManager(siteId, managerId);
      success(response?.data?.message || 'Manager unassigned successfully');
      await fetchSites();
      return { success: true, data: response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to unassign manager';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchSites, success, showError]);

  const value = {
    sites,
    loading,
    fetchSites,
    getSiteById,
    createSite,
    updateSite,
    deleteSite,
    getSiteStats,
    getSiteEmployees,
    getSiteManagers,
    getSiteAttendance,
    assignEmployees,
    unassignEmployees,
    reassignEmployees,
    assignManager,
    unassignManager,
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
